<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

const store = useStore<RootState>();

defineOptions({
  name: 'dataTranslateLanguageGroupItems',
});

// Базовый тип для сообщений с ID перевода элемента языковой группы
type TranslateLanguageGroupItemWithId = {
  idTranslateLanguageGroupItem: number;
};

// Типы для WebSocket сообщений компонента translateLanguageGroupItems
type TranslateLanguageGroupItemsListMessage = WebSocketMessageWithFields<'language-group-item-translations', {
  translateLanguageGroupItems: unknown[];
}>;
type TranslateLanguageGroupItemsListNotFoundMessage = WebSocketMessageEmpty<'language-group-item-list-not-found'>;
type TranslateLanguageGroupItemsTranslationsNotFoundMessage = WebSocketMessageEmpty<'language-group-item-translations-not-found'>;
type TranslateLanguageGroupItemCreateMessage = WebSocketMessageWithFields<'translate-language-group-item-create' | 'translate-language-group-item-create-success', TranslateLanguageGroupItemWithId & {
  idLanguageGroupItem: number;
  idLanguage: number;
  text: string;
}>;
type TranslateLanguageGroupItemUpdateMessage = WebSocketMessageWithFields<'translate-language-group-item-update' | 'translate-language-group-item-update-success', TranslateLanguageGroupItemWithId & {
  text: string;
}>;
type TranslateLanguageGroupItemDeleteMessage = WebSocketMessageWithFields<'translate-language-group-item-delete' | 'translate-language-group-item-delete-success', TranslateLanguageGroupItemWithId>;
type TranslateLanguageGroupItemLockMessage = WebSocketMessageWithFields<'translate-language-group-item-lock' | 'translate-language-group-item-lock-success', TranslateLanguageGroupItemWithId>;
type TranslateLanguageGroupItemUnlockMessage = WebSocketMessageWithFields<'translate-language-group-item-unlock' | 'translate-language-group-item-unlock-success', TranslateLanguageGroupItemWithId>;
type TranslateLanguageGroupItemUpdateGlobalMessage = WebSocketMessageWithFields<'translate-language-group-item-update-global', {
  i18n: {
    groupName: string;
    groupItemCode: string;
    text: string;
  };
}>;

// Объединенный тип для всех сообщений
type TranslateLanguageGroupItemsWebSocketMessage = 
  | TranslateLanguageGroupItemsListMessage 
  | TranslateLanguageGroupItemsListNotFoundMessage 
  | TranslateLanguageGroupItemsTranslationsNotFoundMessage 
  | TranslateLanguageGroupItemCreateMessage 
  | TranslateLanguageGroupItemUpdateMessage 
  | TranslateLanguageGroupItemDeleteMessage 
  | TranslateLanguageGroupItemLockMessage 
  | TranslateLanguageGroupItemUnlockMessage 
  | TranslateLanguageGroupItemUpdateGlobalMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: TranslateLanguageGroupItemsWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'language-group-item-translations':
      store.dispatch('translateLanguageGroupItems/wsSetTranslateLanguageGroupItems', {
        translateLanguageGroupItems: data.translateLanguageGroupItems,
      });
      break;
    case 'language-group-item-list-not-found':
      store.dispatch('languageGroup/wsSetEmptyLanguageGroup');
      break;
    case 'language-group-item-translations-not-found':
      store.dispatch('languageGroupItem/wsSetEmptyLanguageGroupItem');
      store.dispatch('languageGroup/wsSetEmptyLanguageGroup');
      break;

    case 'translate-language-group-item-create':
    case 'translate-language-group-item-create-success':
      store.dispatch('translateLanguageGroupItems/wsTranslateLanguageGroupItemCreate', {
        idTranslateLanguageGroupItem: data.idTranslateLanguageGroupItem,
        idLanguageGroupItem: data.idLanguageGroupItem,
        idLanguage: data.idLanguage,
        text: data.text,
      });
      break;

    case 'translate-language-group-item-update':
    case 'translate-language-group-item-update-success':
      store.dispatch('translateLanguageGroupItems/wsTranslateLanguageGroupItemUpdate', {
        idTranslateLanguageGroupItem: data.idTranslateLanguageGroupItem,
        text: data.text,
      });
      break;

    case 'translate-language-group-item-delete':
    case 'translate-language-group-item-delete-success':
      store.dispatch('translateLanguageGroupItems/wsTranslateLanguageGroupItemDelete', {
        idTranslateLanguageGroupItem: data.idTranslateLanguageGroupItem,
      });
      break;

    case 'translate-language-group-item-lock':
    case 'translate-language-group-item-lock-success':
      store.dispatch('translateLanguageGroupItems/wsTranslateLanguageGroupItemLock', {
        idTranslateLanguageGroupItem: data.idTranslateLanguageGroupItem,
      });
      break;

    case 'translate-language-group-item-unlock':
    case 'translate-language-group-item-unlock-success':
      store.dispatch('translateLanguageGroupItems/wsTranslateLanguageGroupItemUnlock', {
        idTranslateLanguageGroupItem: data.idTranslateLanguageGroupItem,
      });
      break;

    case 'translate-language-group-item-update-global':
      store.dispatch('i18n/updateGroupItemGlobal', {
        groupName: data.i18n.groupName,
        groupItemCode: data.i18n.groupItemCode,
        text: data.i18n.text,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
